import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { SIDE_MENU_DATA } from '@/components/ui/side-menu/side-menu-data';
import SideMenuUI from '@/components/ui/side-menu/side-menu';
import { RootState } from '../../store/store';
import AppHeader from '@/components/ui/app-header';
import MobileHeader from '@/components/ui/mobile-header';
import MobileNavDrawer from '@/components/ui/mobile-nav-drawer';
import { Spinner } from '@/components/ui/spinner';
import { useNavigationService } from '../../services/navigate-service';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSidebarCollapse } from '@/hooks/useSidebarCollapse';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';

const AppShell = () => {
  // At or above the shared breakpoint: today's header + sidebar; below it,
  // MobileHeader + MobileNavDrawer (spec 2026-08-10).
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);
  const { collapsed, toggle: toggleSidebar } = useSidebarCollapse();
  const { navigateTo } = useNavigationService();
  const isLogin = localStorage.getItem('accessToken');
  const [isLoading, setIsLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const activeRoute = useSelector(
    (state: RootState) => state.router.activeRoute,
  );

  useEffect(() => {
    if (activeRoute) navigateTo(`/home/${activeRoute}`);
    // Reacts to Redux-driven activeRoute changes; navigateTo is a stable
    // reference returned by useNavigationService.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoute]);

  return (
    // h-dvh (not vh): iOS Safari's URL bar makes 100vh taller than the
    // visible viewport; dvh tracks it. No overflow clip here by design —
    // the shell must genuinely not overflow (Unit 1 spec).
    <div className="flex h-dvh flex-col">
      {isDesktop ? (
        <AppHeader
          isLogin={isLogin !== null}
          setIsLoading={setIsLoading}
          sidebarCollapsed={collapsed}
          onToggleSidebar={toggleSidebar}
        />
      ) : (
        <MobileHeader
          isLogin={isLogin !== null}
          onOpenNav={() => setNavOpen(true)}
        />
      )}
      <section className="flex min-h-0 w-full flex-1">
        {isDesktop && (
          <SideMenuUI sideMenuData={SIDE_MENU_DATA} collapsed={collapsed} />
        )}
        {isLoading ? (
          <div className="relative min-w-0 flex-1 overflow-auto bg-background flex justify-center items-center">
            <Spinner size="lg" />
          </div>
        ) : (
          // `relative` is required, not decorative: `overflow-auto` does not
          // establish a containing block, so without it any `position:
          // absolute` page root rendered through <Outlet/> (e.g. PaymentPage's
          // non-cart branch) resolves against the viewport instead of <main>.
          <main className="relative min-w-0 flex-1 overflow-auto bg-background">
            <Outlet />
          </main>
        )}
      </section>
      {!isDesktop && (
        <MobileNavDrawer
          open={navOpen}
          onOpenChange={setNavOpen}
          isLogin={isLogin !== null}
          setIsLoading={setIsLoading}
        />
      )}
    </div>
  );
};

export default AppShell;
